import React, { FC, useMemo } from 'react';
import { Canvas2D } from './Canvas2D';
import { Vector } from '../types';
import { monogramBitmap } from './monogramBitmap';

export type DialogProps = {
  position: Vector;
  rotation?: Vector;
  size: [number, number];
  canvasSize: [number, number];
  text: string;
  textRef?: { current: string };
  center?: boolean;
  right?: boolean;
  onComplete?: () => void;
  background?: string;
  color?: [number, number, number, number];
  meshRef?: (el: any) => void;
  visible?: boolean;
  opacity?: number;
};

export const Dialog: FC<DialogProps> = ({
  text,
  textRef,
  center,
  right,
  position,
  rotation,
  size,
  canvasSize,
  canvasSize: [canvasWidth, canvasHeight],
  background,
  color: [r, g, b, a] = [0, 0, 0, 255],
  meshRef,
  visible = true,
  opacity = 1,
}) => {
  const draw = useMemo(() => {
    let done = false;
    let _text = textRef?.current ?? text;
    return (ctx: CanvasRenderingContext2D): boolean => {
      if (textRef === undefined) {
        if (done) return false;
        done = true;
      } else {
        if (textRef.current === _text) return false;
        _text = textRef.current;
      }

      if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      } else {
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      }
      const imageData = ctx.getImageData(0, 0, canvasWidth, canvasHeight);

      const letterWidth = 6;
      const letterHeight = 10;
      const padding = 5;
      const w = canvasWidth - 2 * padding;

      const lines = [];
      const paragraphs = _text.split('\n');
      for (let i = 0; i < paragraphs.length; i++) {
        const words = paragraphs[i].split(' ');
        const maxLineLength = Math.floor(w / letterWidth);
        let line = words.shift() ?? '';
        let next;
        while (line && (next = words.shift())) {
          if (line.length + next.length + 1 < maxLineLength) {
            line += ` ${next}`;
          } else {
            lines.push(line);
            line = next;
          }
        }
        lines.push(line);
      }

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const xOffset = center
          ? Math.floor((canvasWidth - line.length * letterWidth) / 2)
          : right
          ? canvasWidth - line.length * letterWidth - padding
          : padding;
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          const bmp = monogramBitmap[char];
          if (bmp) {
            for (let k = 0; k < bmp.length; k++) {
              const bmpRow = bmp[k].toString(2).padStart(letterWidth, '0');
              for (let l = 0; l < bmpRow.length; l++) {
                if (bmpRow[bmpRow.length - l - 1] === '1') {
                  let x = xOffset + letterWidth * j + l;
                  let y = padding + letterHeight * i + k;
                  const pos = (y * canvasWidth + x) * 4;
                  imageData.data[pos] = r;
                  imageData.data[pos + 1] = g;
                  imageData.data[pos + 2] = b;
                  imageData.data[pos + 3] = a;
                }
              }
            }
          }
        }
      }
      ctx.putImageData(imageData, 0, 0);

      return true;
    };
  }, [
    text,
    center,
    canvasWidth,
    canvasHeight,
    background,
    r,
    g,
    b,
    a,
    right,
    textRef,
  ]);

  return (
    <Canvas2D
      draw={draw}
      position={position}
      rotation={rotation}
      size={size}
      canvasSize={canvasSize}
      meshRef={meshRef}
      visible={visible}
      opacity={opacity}
    />
  );
};
